import "./SwipeableModals.scss";
import React, { useState, useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";

import { toast } from "react-toastify";

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  editData,
  onSubmitSuccess,
  categories,
}) => {
  // Form state
  const [department, setDepartment] = useState("");
  const [services, setServices] = useState([
    { nameAr: "", nameEn: "", price: "" },
  ]);
  const [loading, setLoading] = useState(false);
  // console.log(first)
  // Reset form when modal opens or edit data changes
  useEffect(() => {
    if (editData && currentView === "edit") {
      setDepartment(editData.category._id || "");

      // For edit mode, we're dealing with a single service
      setServices([
        {
          nameAr: editData.name?.ar || "",
          nameEn: editData.name?.en || "",
          price: editData.price?.toString() || "",
        },
      ]);
    } else if (currentView === "create") {
      // Reset form for create mode
      setDepartment("");
      setServices([{ nameAr: "", nameEn: "", price: "" }]);
    }
  }, [editData, currentView, open]);

  // Handle remove service field (only for create mode)
  const handleRemoveService = (index) => {
    if (currentView === "create" && services.length > 1) {
      const updatedServices = [...services];
      updatedServices.splice(index, 1);
      setServices(updatedServices);
    }
  };

  // Handle service field change
  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...services];
    updatedServices[index][field] = value;
    setServices(updatedServices);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form
      if (!department) {
        toast.error("يرجى اختيار القسم");
        setLoading(false);
        return;
      }

      // Use only the first service in the array
      const service = services[0];

      // Validate service has name and price
      if (!service.nameAr.trim() || !service.price.trim()) {
        toast.error("يرجى إدخال اسم وسعر الخدمة");
        setLoading(false);
        return;
      }

      // Prepare data for API - single service format
      const formData = {
        name: {
          ar: service.nameAr,
          en: service.nameEn || service.nameAr, // Use Arabic name as fallback for English
        },
        price: parseFloat(service.price),
        category: department,
      };

      let response;

      if (currentView === "edit" && editData?._id) {
        // Update existing service
        response = await api.put(`/services/${editData._id}`, formData);
      } else {
        // Create new service
        response = await api.post("/services", formData);
      }

      if (response.data.status === "success") {
        onSubmitSuccess();
        toast.success(
          currentView === "edit"
            ? "تم تعديل الخدمة بنجاح"
            : "تم إنشاء الخدمة بنجاح"
        );
        onClose(); // Close modal after successful submission
      }
    } catch (error) {
      console.error("Error saving service:", error);
      toast.error(
        error.response?.data?.message ||
          "حدث خطأ أثناء حفظ الخدمة، يرجى المحاولة مرة أخرى"
      );
    } finally {
      setLoading(false);
    }
  };

  // Render Create/Edit Service View
  const renderServiceForm = () => {
    const isEdit = currentView === "edit";
    const title = isEdit ? "تعديل خدمة" : "اضافة خدمات جديدة";
    const buttonText = isEdit ? "تعديل" : "اضافة";

    return (
      <div className="user-modal">
        <p className="user-modal-p">{title}</p>
        <form onSubmit={handleSubmit}>
          {/* Department/Category Select */}
          <FormControl
            sx={{
              width: "100%",
            }}
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              الاقسام
            </InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="">
                <em>اختر القسم</em>
              </MenuItem>
              {categories?.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category?.name?.ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Service Items */}
          {services.map((service, index) => (
            <div
              key={index}
              className="service-item"
              style={{ marginBottom: "20px", position: "relative" }}
            >
              {/* Arabic Name */}
              <FormControl sx={{ width: "100%" }} variant="outlined">
                <InputLabel
                  htmlFor={`name-ar-${index}`}
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  اسم الخدمة بالعربية
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  required
                  id={`name-ar-${index}`}
                  type="text"
                  notched
                  value={service.nameAr}
                  onChange={(e) =>
                    handleServiceChange(index, "nameAr", e.target.value)
                  }
                />
              </FormControl>

              {/* English Name */}
              <FormControl
                sx={{ width: "100%", marginTop: "10px" }}
                variant="outlined"
              >
                <InputLabel
                  htmlFor={`name-en-${index}`}
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  اسم الخدمة بالانجليزية
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  id={`name-en-${index}`}
                  type="text"
                  notched
                  value={service.nameEn}
                  onChange={(e) =>
                    handleServiceChange(index, "nameEn", e.target.value)
                  }
                />
              </FormControl>

              {/* Price */}
              <FormControl
                sx={{ width: "100%", marginTop: "10px" }}
                variant="outlined"
              >
                <InputLabel
                  htmlFor={`price-${index}`}
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  سعر الخدمة
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  required
                  id={`price-${index}`}
                  type="text"
                  notched
                  placeholder="ريال"
                  value={service.price}
                  onChange={(e) =>
                    handleServiceChange(index, "price", e.target.value)
                  }
                />
              </FormControl>

              {/* Remove service button - only show for create mode and when there's more than one service */}
              {currentView === "create" && services.length > 1 && (
                <IconButton
                  aria-label="delete"
                  onClick={() => handleRemoveService(index)}
                  sx={{ position: "absolute", top: 0, left: 0, color: "red" }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          ))}

          {/* Add service button - only show for create mode */}

          <BlueButton
            text={buttonText}
            size="medium"
            variant="fill"
            type="submit"
            loading={loading}
          />
        </form>
      </div>
    );
  };

  const renderDefaultView = () => <div></div>;

  const views = {
    create: renderServiceForm,
    edit: renderServiceForm,
  };

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {(views[currentView] || renderDefaultView)()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;
